/**
 * BlockRateSparkline — a tiny, dependency-free inline SVG sparkline of the
 * daily guardrail block-rate (blocked / (blocked + allowed)), for the
 * Operations "block-rate" metric card (P5 Wave 4 contract §3). No chart library
 * — the whole point is a lightweight glyph, matching the mockup.
 *
 * Points arrive oldest-first. Each value is a rate in [0,1]; the y-axis is
 * fixed to [0,1] so the line's height is comparable across renders rather than
 * auto-scaled to the local min/max. Renders nothing meaningful (a flat baseline)
 * when there is <2 points.
 */

import React from 'react';
import { Box } from '@mui/material';

export interface BlockRateSparklineProps {
  /** Daily block-rates, oldest → newest, each in [0,1]. */
  values: number[];
  width?: number;
  height?: number;
  color?: string;
}

const BlockRateSparkline: React.FC<BlockRateSparklineProps> = ({
  values,
  width = 96,
  height = 28,
  color = '#7D1935', // burgundy (colors.costs.main)
}) => {
  const pad = 2;
  const w = width - pad * 2;
  const h = height - pad * 2;

  const clean = values.map((v) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0));

  if (clean.length < 2) {
    // Not enough data to draw a trend — render a flat baseline.
    return (
      <Box aria-hidden sx={{ display: 'inline-flex' }}>
        <svg width={width} height={height} role="img" aria-label="Block-rate sparkline (insufficient data)">
          <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke={color} strokeOpacity={0.3} strokeWidth={1.5} />
        </svg>
      </Box>
    );
  }

  const stepX = w / (clean.length - 1);
  const toPoint = (v: number, i: number) => {
    const x = pad + i * stepX;
    const y = pad + (1 - v) * h; // invert: higher rate = higher on chart
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  };

  const linePoints = clean.map(toPoint).join(' ');
  const areaPoints = `${pad},${height - pad} ${linePoints} ${width - pad},${height - pad}`;
  const last = clean[clean.length - 1];
  const lastX = pad + (clean.length - 1) * stepX;
  const lastY = pad + (1 - last) * h;

  return (
    <Box sx={{ display: 'inline-flex' }}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`Block-rate sparkline, latest ${(last * 100).toFixed(1)}%`}
      >
        <polygon points={areaPoints} fill={color} fillOpacity={0.12} />
        <polyline
          points={linePoints}
          fill="none"
          stroke={color}
          strokeWidth={1.75}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <circle cx={lastX} cy={lastY} r={2.25} fill={color} />
      </svg>
    </Box>
  );
};

export default BlockRateSparkline;
